/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190729 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { Resetable } from '../../../resetter/resetable';
import { DynForm } from 'src/app/_model/dyn-form';
import { IMSId, IMSObject, IMSObjectType } from 'src/app/_model/ims-object';
import { DynFormData } from 'src/app/_model/dyn-form-data';

@Injectable()
export abstract class DynFormDataDaoService implements Resetable {
  public abstract saveDynFormData(
    dynFormData: DynFormData,
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
  ): Promise<number>;
  public abstract updateACInfo(
    dynFormDataId: number,
    acukey: string,
    acid: number,
    chainRootAcId?: number,
    acStatus?: number,
    acIsLocked?: boolean,
    draftOwnerEmpId?: number,
    draftOwnerMcId?: number,
    draftOwnerServiceJobId?: number,
    cloudSyncState?: number,
  ): Promise<void>;
  /**
   * APP4-948: updates the tristate cloud-sync state of all unfinished local drafts of
   * the given chain - see hasNotFinishedDynFormDataForDraft for the matching keys.
   */
  public abstract updateCloudSyncStateByDraft(
    acukey: string,
    acid: number,
    cloudSyncState: number,
    chainRootAcId?: number,
  ): Promise<void>;
  /**
   * APP4-948: true if an unfinished local draft of the given draft chain exists. The acukey is
   * the stable identity of the whole AC version chain (every autosave creates a new acid, the
   * acukey stays); the acid is checked as fallback for rows without a persisted acukey.
   */
  public abstract hasNotFinishedDynFormDataForDraft(
    acukey: string,
    acid: number,
    chainRootAcId?: number,
  ): Promise<boolean>;
  /**
   * APP4-948: `isDirty` means "carries changes that have not been handed to the upload
   * queue yet", so it has to be cleared once a cloud autosave was queued - otherwise a
   * locally created draft stays dirty forever and can never be cleaned up.
   */
  public abstract updateIsDirty(
    formDataId: number,
    isDirty: boolean,
  ): Promise<void>;
  /**
   * APP4-948: the persisted chain identity of a form. The upload response writes it via
   * updateACInfo, which an in-memory copy of the DynFormData does not see - so an upload
   * has to ask the DB rather than trust its own state, see queueCloudAutosave.
   */
  public abstract getAcIdentity(
    formDataId: number,
  ): Promise<{ acukey?: string; acid?: number; chainRootAcId?: number }>;
  /**
   * APP4-948: deletes the unedited local drafts of the given chain — called when the chain
   * is finished/no longer a draft, so passive copies disappear on every device.
   *
   * Includes drafts CREATED on this device, not just ones restored from the cloud: the
   * creating device used to keep a phantom draft forever, because only restored mirrors
   * were cleaned up. Dirty rows are always kept so offline work survives as a conflict
   * version.
   */
  /**
   * KRK-45: `chainRootAcId` is the identity of the AC version chain and the key to match
   * on; `acukey` is only a fallback for rows written before it existed (a smart
   * renumbering rewrites the acukey), `acid` one for rows that have neither.
   */
  /**
   * KRK-45: removes every unfinished local row of the chain, edited ones included - only for a
   * real deletion, see the implementation.
   */
  public abstract deleteDraftsForChain(
    chainRootAcId: number | undefined,
    acukey: string | undefined,
  ): Promise<void>;
  public abstract deleteNotDirtyDraftsForChain(
    chainRootAcId: number | undefined,
    acukey: string | undefined,
  ): Promise<void>;
  public abstract deleteDynFormData(dynFormData: DynFormData): Promise<boolean>;
  public abstract getDynFormData(
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
    forceFinished?: boolean,
  ): Promise<DynFormData>;
  public abstract getSpecificDynFormDataFor(
    dynFormId: IMSId,
    obj: IMSObject,
    objRef: IMSObjectType,
    additionalDataKey: string,
    mode: string,
  ): Promise<DynFormData>;
  public abstract getNotFinishedDynFormData(
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
  ): Promise<DynFormData[]>;
  public abstract getNotFinishedJobSectionDynFormData(
    dynForm: DynForm,
    obj: IMSObject,
  ): Promise<DynFormData[]>;
  public abstract hasFinishedNotExpiredFormData(
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
    maxAgeDays?: number,
  ): Promise<boolean>;
  public abstract getAmountOfFinishedDynFormDatasForObjIdSince(
    dynFormId: IMSId,
    obj: IMSObject,
    additionalDataKey: string,
    timestamp: number,
  ): Promise<number>;
  public abstract createNewFormData(
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
  ): Promise<DynFormData>;
  public abstract getAllDynFormDatasForObj(
    obj: IMSObject,
  ): Promise<DynFormData[]>;
  public abstract getDynFormDataByExtFileId(
    extFileId: string,
  ): Promise<DynFormData | undefined>;
  public abstract getDynFormDataByFormIdAndReferenceTs(
    formId: number,
    referenceTs: number,
  ): Promise<DynFormData | undefined>;
  public abstract reset(): Promise<void>;
  public abstract clear(): Promise<void>;
}
